import React, { useEffect } from "react";
import {
    verifyReportMFA,
    signAndLockReport,
    SignatureReason,
    SignAndLockResponseFromAPI,
} from "../../../api/report";
import AppButton from "../../AppButton";
import OTPInput, {
    OTPInputRef,
} from "../../../routes/auth-flow/components/OTPInput";
import ErrorMessage from "../../../routes/auth-flow/components/ErrorMessage";
import BaseDialog from "../../BaseDialog";
import { twMerge } from "tailwind-merge";

type Props = {
    testId: string;
    reason: SignatureReason;
    onSuccess: (report: any) => void;
    onClose: () => void;
    onResendCode: () => Promise<void>;
    isOpen: boolean;
};

function ReportMFADialog({
    testId,
    reason,
    onSuccess,
    onClose,
    onResendCode,
    isOpen,
}: Props) {
    const [otp, setOtp] = React.useState<string[]>(Array(6).fill(""));
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState("");
    const [countdown, setCountdown] = React.useState(0);
    const [countdownEndTime, setCountdownEndTime] = React.useState<
        number | null
    >(null);
    const [isResending, setIsResending] = React.useState(false);
    const otpInputRef = React.useRef<OTPInputRef>(null);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdownEndTime) {
            const updateCountdown = () => {
                const now = Date.now();
                const remainingTime = Math.max(
                    0,
                    Math.ceil((countdownEndTime - now) / 1000)
                );
                setCountdown(remainingTime);

                if (remainingTime > 0) {
                    timer = setTimeout(updateCountdown, 1000);
                } else {
                    setCountdownEndTime(null);
                }
            };
            updateCountdown();
        } else {
            setCountdown(0);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [countdownEndTime]);

    useEffect(() => {
        if (!isOpen) {
            // Reset state when dialog is closed
            setOtp(Array(6).fill(""));
            setSubmitting(false);
            setError("");
        } else {
            // Focus the first OTP input when dialog opens
            otpInputRef.current?.focus();
        }
    }, [isOpen]);

    const handleSendCodeAgain = async () => {
        if (countdown > 0) return;

        setIsResending(true);
        setError("");
        try {
            await onResendCode();
            const endTime = Date.now() + 60 * 1000; // 60 seconds from now
            setCountdownEndTime(endTime);
        } catch (error) {
            setError(
                error instanceof Error ? error.message : "Failed to send code"
            );
        } finally {
            setIsResending(false);
        }
    };

    const submit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (otp.filter(Boolean).length < 6) return null;

        setSubmitting(true);
        setError("");
        try {
            const code = otp.join("");
            await verifyReportMFA(code);

            // After successful MFA verification, call sign-and-lock again
            const result = (await signAndLockReport({
                testId,
                reason,
            })) as SignAndLockResponseFromAPI;

            // Check if it's a Report object (successful signing)
            if ("status" in result) {
                onSuccess(result);
            } else {
                throw new Error("Unexpected response after MFA verification");
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : "Wrong OTP code");
        } finally {
            setSubmitting(false);
        }
    };

    const sendAnotherCodeIsDisabled = countdown > 0 || isResending;

    return (
        <BaseDialog
            isOpen={isOpen}
            onClose={onClose}
            title="Two-Factor Authentication"
            classes={{
                dialogPanel: "w-full max-w-md h-auto max-h-none",
            }}
        >
            <form className="mt-0" onSubmit={submit}>
                <p className="mb-10">
                    Please enter 6-digit verification code sent via SMS.
                </p>

                <OTPInput
                    className=""
                    otp={otp}
                    ref={otpInputRef}
                    setOtp={setOtp}
                />

                <button
                    type="button"
                    className={twMerge(
                        `text-sm text-left text-gray-medium`,
                        sendAnotherCodeIsDisabled
                            ? "opacity-50 cursor-not-allowed"
                            : "underline hover:no-underline"
                    )}
                    onClick={handleSendCodeAgain}
                    disabled={sendAnotherCodeIsDisabled}
                >
                    {isResending
                        ? "Sending code..."
                        : countdown > 0
                        ? `Send me another code (${countdown}s)`
                        : "Don't work? Send me another code."}
                </button>

                <div className="flex flex-col gap-2 mt-10">
                    <ErrorMessage message={error} className="mb-0.5" />
                    <div className="flex gap-3">
                        <AppButton
                            type="button"
                            onClick={onClose}
                            variant="outlined"
                            className="flex-1"
                        >
                            Cancel
                        </AppButton>
                        <AppButton
                            className="flex-1"
                            type="submit"
                            disabled={otp.some((el) => el.length === 0)}
                            loading={submitting}
                        >
                            Verify & Sign
                        </AppButton>
                    </div>
                </div>
            </form>
        </BaseDialog>
    );
}

export default ReportMFADialog;
